/**
 * AuditEventSheet (TRA-1626): one audit log row, opened. Who, what, which
 * thing, when and from where, then the before and after as a field list
 * with the changed fields first. `AuditEventDetail` is what the sheet
 * holds, kept apart so the section test can render it without the portal.
 */

import { MONO } from '@/components/models/home/RequestDetailContent'
import { Badge } from '@/components/ui/badge'
import { EYEBROW } from '@/components/ui/eyebrow'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import type { AuditEvent } from '@/hooks/useAuditEvents'

import { ActorCell } from './ActorCell'
import {
  actionLabel,
  fieldRows,
  formatValue,
  formatWhen,
  OUTCOME_LABELS,
  outcomeVariant,
  resourceLabel,
} from './auditLabels'

function Fact({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="grid grid-cols-[8rem_minmax(0,1fr)] gap-x-4 gap-y-1 border-b border-border py-2 text-sm">
      <dt className={EYEBROW}>{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  )
}

export function AuditEventDetail({ event }: { event: AuditEvent }): React.JSX.Element {
  const rows = fieldRows(event)
  return (
    <div className="space-y-6">
      <dl>
        <Fact label="Who">
          <ActorCell event={event} />
        </Fact>
        <Fact label="Action">
          <span>{actionLabel(event.action)}</span>
          <span className={`ml-2 ${MONO} text-muted-foreground`}>{event.action}</span>
        </Fact>
        {resourceLabel(event) !== '' && (
          <Fact label="Resource">
            <span className={MONO}>{resourceLabel(event)}</span>
          </Fact>
        )}
        <Fact label="Result">
          <Badge variant={outcomeVariant(event.outcome)}>{OUTCOME_LABELS[event.outcome]}</Badge>
          {event.status_code !== null && (
            <span className={`ml-2 ${MONO} text-muted-foreground`}>HTTP {event.status_code}</span>
          )}
        </Fact>
        <Fact label="When">{formatWhen(event.occurred_at)}</Fact>
        {event.client_ip !== null && (
          <Fact label="Address">
            <span className={MONO}>{event.client_ip}</span>
          </Fact>
        )}
        {event.request_id !== null && (
          <Fact label="Request id">
            <span className={MONO}>{event.request_id}</span>
          </Fact>
        )}
        {event.user_agent !== null && (
          <Fact label="Browser">
            <span className="text-xs text-muted-foreground">{event.user_agent}</span>
          </Fact>
        )}
      </dl>
      <section className="space-y-3">
        <h3 className="font-serif text-base font-medium text-foreground">Before and after</h3>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">This event changed nothing. It is a record of a view.</p>
        ) : (
          <ul className="divide-y divide-border border-y border-border">
            {rows.map((row) => (
              <li key={row.field} className="grid gap-2 py-3 text-sm md:grid-cols-[10rem_minmax(0,1fr)_minmax(0,1fr)]">
                <span className="flex flex-wrap items-center gap-2">
                  <span className={MONO}>{row.field}</span>
                  {row.changed && <Badge variant="outline">Changed</Badge>}
                </span>
                <span className="min-w-0">
                  <span className={EYEBROW}>Before</span>
                  <pre className="whitespace-pre-wrap break-words font-mono text-xs text-muted-foreground">
                    {formatValue(row.before)}
                  </pre>
                </span>
                <span className="min-w-0">
                  <span className={EYEBROW}>After</span>
                  <pre className="whitespace-pre-wrap break-words font-mono text-xs text-foreground">
                    {formatValue(row.after)}
                  </pre>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

interface AuditEventSheetProps {
  /** The open row; null keeps the sheet closed. */
  event: AuditEvent | null
  onClose: () => void
}

export function AuditEventSheet({ event, onClose }: AuditEventSheetProps): React.JSX.Element {
  return (
    <Sheet
      open={event !== null}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-2xl">
        <SheetHeader className="mb-6">
          <SheetTitle>Audit event</SheetTitle>
          <SheetDescription>See who did what to this team's data and what it was before.</SheetDescription>
        </SheetHeader>
        {event === null ? null : <AuditEventDetail key={event.id} event={event} />}
      </SheetContent>
    </Sheet>
  )
}
